import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2, Pencil, Info, Truck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtEuro, parseNum } from "@/lib/dates";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function AdminSettings({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [markup, setMarkup] = useState("");
  const [thHigh, setThHigh] = useState("");
  const [thLow, setThLow] = useState("");
  const [amorts, setAmorts] = useState([]);
  const [activeJobs, setActiveJobs] = useState(0);
  const [savingCfg, setSavingCfg] = useState(false);

  const [amDesc, setAmDesc] = useState("");
  const [amAmount, setAmAmount] = useState("");
  const [addingAmort, setAddingAmort] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editDesc, setEditDesc] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [busyEdit, setBusyEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, j] = await Promise.all([api.get("/settings"), api.get("/jobs")]);
      setMarkup(String(Math.round((s.data.cost_multiplier - 1) * 100)));
      setThHigh(String(s.data.threshold_high).replace(".", ","));
      setThLow(String(s.data.threshold_low).replace(".", ","));
      setAmorts(s.data.amortizations || []);
      setActiveJobs(j.data.length);
    } catch {
      toast.error("Errore nel caricamento delle impostazioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const multiplier = 1 + parseNum(markup) / 100;

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/settings", {
        cost_multiplier: multiplier,
        threshold_high: parseNum(thHigh),
        threshold_low: parseNum(thLow),
      });
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingCfg(false);
    }
  };

  const reloadAmorts = async () => {
    const s = await api.get("/settings");
    setAmorts(s.data.amortizations || []);
  };

  const addAmort = async () => {
    if (!amDesc.trim()) return toast.error("Inserisci una descrizione");
    setAddingAmort(true);
    try {
      await api.post("/amortizations", { description: amDesc, annual_amount: parseNum(amAmount) });
      setAmDesc("");
      setAmAmount("");
      await reloadAmorts();
      toast.success("Voce aggiunta");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAddingAmort(false);
    }
  };

  const toggleAmort = async (a) => {
    try {
      await api.put(`/amortizations/${a.id}`, { active: !a.active });
      await reloadAmorts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteAmort = async (a) => {
    try {
      await api.delete(`/amortizations/${a.id}`);
      await reloadAmorts();
      toast.success("Voce eliminata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openEditAmort = (a) => {
    setEditTarget(a);
    setEditDesc(a.description);
    setEditAmount(String(a.annual_amount).replace(".", ","));
  };

  const saveEditAmort = async () => {
    if (!editDesc.trim()) return toast.error("Inserisci una descrizione");
    setBusyEdit(true);
    try {
      await api.put(`/amortizations/${editTarget.id}`, { description: editDesc, annual_amount: parseNum(editAmount) });
      setEditTarget(null);
      await reloadAmorts();
      toast.success("Voce aggiornata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyEdit(false);
    }
  };

  const totalActiveAnnual = amorts.filter((a) => a.active).reduce((s, a) => s + (a.annual_amount || 0), 0);
  const quotaPerJob = activeJobs > 0 ? totalActiveAnnual / activeJobs / 12 : 0;

  const inputCls = "w-full h-14 rounded-xl border border-slate-200 px-4 text-lg outline-none focus:border-primary";
  const smallInput = "w-full h-12 rounded-xl border border-slate-200 px-3 text-base outline-none focus:border-primary";

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="flex items-center gap-2">
        <button data-testid="settings-back-button" onClick={onClose} className="h-10 w-10 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 active:scale-95">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-head text-2xl font-bold text-slate-900">Impostazioni</h1>
      </div>

      {/* Cost multiplier */}
      <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <h2 className="font-head font-bold text-lg text-slate-900">Costo dipendente</h2>
        <div>
          <label className="text-sm font-semibold text-slate-600 mb-1 block">Maggiorazione oneri datore di lavoro (%)</label>
          <input data-testid="markup-input" inputMode="decimal" className={inputCls} placeholder="38" value={markup} onChange={(e) => setMarkup(e.target.value)} />
        </div>
        <div className="rounded-xl bg-accent/60 p-3 text-sm text-accent-foreground">
          Costo orario reale = retribuzione lorda × <span className="font-bold" data-testid="multiplier-preview">{multiplier.toLocaleString("it-IT", { maximumFractionDigits: 2 })}</span>
          <br />
          <span className="text-slate-500">Es. 10,00 €/h lordi → {fmtEuro(10 * multiplier)}/h di costo reale.</span>
        </div>
        <div className="flex gap-2 text-xs text-slate-500 bg-slate-50 rounded-xl p-3">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <p>Stima realistica degli oneri a carico del datore (INPS datore, INAIL, accantonamento TFR e altri oneri minori). Il valore esatto va verificato con il tuo consulente del lavoro/commercialista e può variare in base al CCNL applicato e alle eventuali agevolazioni.</p>
        </div>
      </section>

      {/* Thresholds */}
      <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <h2 className="font-head font-bold text-lg text-slate-900">Soglie margine</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1 block">Soglia alta (%)</label>
            <input data-testid="threshold-high-input" inputMode="decimal" className={smallInput} placeholder="20" value={thHigh} onChange={(e) => setThHigh(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-1 block">Soglia bassa (%)</label>
            <input data-testid="threshold-low-input" inputMode="decimal" className={smallInput} placeholder="0" value={thLow} onChange={(e) => setThLow(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-emerald-500" /> Verde: margine ≥ soglia alta (buon guadagno)</div>
          <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-amber-500" /> Giallo: tra soglia bassa e alta (poco guadagno)</div>
          <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-500" /> Rosso: sotto la soglia bassa (in perdita)</div>
        </div>
      </section>

      <button data-testid="save-settings-button" onClick={saveCfg} disabled={savingCfg} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60">
        {savingCfg ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva impostazioni"}
      </button>

      {/* Amortizations */}
      <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-primary" />
          <h2 className="font-head font-bold text-lg text-slate-900">Ammortamenti</h2>
        </div>
        <p className="text-xs text-slate-500">Il totale annuo delle voci attive viene diviso per il numero di lavori attivi e per 12 mesi, ottenendo la quota mensile imputata a ogni lavoro.</p>

        <div className="space-y-2">
          {amorts.length === 0 && <p className="text-sm text-slate-400">Nessuna voce. Aggiungi la prima (es. Furgone).</p>}
          {amorts.map((a) => (
            <div key={a.id} data-testid="amort-item" className={`border rounded-xl p-3 flex items-center justify-between gap-2 ${a.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate">{a.description}</p>
                <p className="text-sm text-slate-500">{fmtEuro(a.annual_amount)}/anno</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button data-testid={`toggle-amort-${a.id}`} onClick={() => toggleAmort(a)} className={`h-9 px-2.5 rounded-lg border text-xs font-bold active:scale-95 ${a.active ? "border-primary text-primary" : "border-slate-200 text-slate-400"}`}>
                  {a.active ? "Attiva" : "Sospesa"}
                </button>
                <button data-testid={`edit-amort-${a.id}`} onClick={() => openEditAmort(a)} className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 flex items-center justify-center active:scale-95">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`delete-amort-${a.id}`} onClick={() => deleteAmort(a)} className="h-9 w-9 rounded-lg border border-red-200 text-destructive flex items-center justify-center active:scale-95">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl bg-accent/60 p-3 text-sm text-accent-foreground">
          <div className="flex justify-between"><span>Totale annuo attivo</span><span className="font-bold">{fmtEuro(totalActiveAnnual)}</span></div>
          <div className="flex justify-between"><span>Lavori attivi</span><span className="font-bold">{activeJobs}</span></div>
          <div className="flex justify-between border-t border-teal-200 mt-1 pt-1"><span>Quota mensile per lavoro</span><span className="font-bold" data-testid="quota-preview">{fmtEuro(quotaPerJob)}</span></div>
        </div>

        <div className="border-t border-slate-100 pt-3 space-y-2">
          <p className="text-sm font-semibold text-slate-600">Nuova voce</p>
          <input data-testid="amort-desc-input" className={smallInput} placeholder="Descrizione (es. Furgone)" value={amDesc} onChange={(e) => setAmDesc(e.target.value)} />
          <input data-testid="amort-amount-input" inputMode="decimal" className={smallInput} placeholder="Importo annuo (es. 5000)" value={amAmount} onChange={(e) => setAmAmount(e.target.value)} />
          <button data-testid="add-amort-button" onClick={addAmort} disabled={addingAmort} className="w-full h-12 rounded-xl border border-primary text-primary font-bold flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60">
            {addingAmort ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Plus className="h-5 w-5" /> Aggiungi voce</>}
          </button>
        </div>
      </section>

      {/* Edit amortization dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">Modifica voce</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <input data-testid="edit-amort-desc" className={inputCls} placeholder="Descrizione" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
            <input data-testid="edit-amort-amount" inputMode="decimal" className={inputCls} placeholder="Importo annuo" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} />
          </div>
          <DialogFooter>
            <button data-testid="save-amort-edit-button" onClick={saveEditAmort} disabled={busyEdit} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60">
              {busyEdit ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
